import {Formik} from 'formik';

import {FormComponent} from './FormComponent';

import {RecordSchema} from './RecordSchema';

import {FormWithButtons} from '@flexinet/ui-components';

import {SetupIntent} from '../types';
import {stage} from "../../../config/stage";


export const RecordForm = ({
                               record,
                               handleSubmit,
                               handleCancel,
                           }: {
    record: Omit<SetupIntent, 'id'>,
    // Returns a promise now that the mutations are `mutateAsync`; typed to
    // accept either so a non-async caller still compiles.
    handleSubmit: (values: SetupIntent) => void | Promise<unknown>,
    handleCancel?: () => void
}) => {

    /**
     * ⚠️ FNP-918: this AWAITS the submit handler.
     *
     * It used to call `handleSubmit(values)` and return immediately, dropping
     * the promise. Formik then cleared `isSubmitting` on the next tick, so
     * `disabled={isSubmitting}` on the submit button never engaged for the
     * duration of the request — the button stayed live and a second click sent
     * a second mutation. On a money-path create that is a duplicate setup
     * intent, which is why this matters more here than the missing spinner.
     *
     * The handler is now `mutateAsync`, which REJECTS on failure. The catch is
     * deliberate and must not be removed: without it an unhandled rejection
     * escapes and Formik never resets `isSubmitting`, leaving the form wedged
     * with the button permanently disabled. The user-facing error is already
     * raised by the mutation's `onError` notification, so this only needs to
     * stop the rejection propagating.
     */
    const onSubmit = async (values: SetupIntent) => {
        try {
            await handleSubmit(values);
        } catch {
            // Reported by the mutation's onError notification.
        }
    };

    const onCancel = () => {
        handleCancel?.();
    };


    let initialValues = {
        customerId: '',
        description: '',
        paymentMethod: '',
        usage: 'offSession',
    } as SetupIntent;

    if (record) {
        initialValues = Object.assign({}, initialValues, record);
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={RecordSchema}
            onSubmit={onSubmit}
        >
            {({
                  handleSubmit,
                  isSubmitting,
                  ...rest
              }) => (
                <FormWithButtons handleSubmit={handleSubmit} isSubmitting={isSubmitting}
                                 handleCancel={onCancel} debug={false && stage === 'ide'} {...rest}  >
                    <FormComponent/>
                </FormWithButtons>)}
        </Formik>
    );
};
